import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../axiosInterceptor";
import { toast } from "react-toastify";
import "./productDetail.css";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace(/am|pm/i, (m) => m.toUpperCase());
};

const toCurrency = (value) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return "-";
  return `$${Number(value).toFixed(2)}`;
};

const getDiscountPercent = (price, compareAt) => {
  const p = Number(price);
  const c = Number(compareAt);
  if (!Number.isFinite(p) || !Number.isFinite(c) || c <= 0 || p <= 0 || c <= p) return null;
  return Math.round(((c - p) / c) * 100);
};

const normalizeProduct = (raw) => {
  if (!raw) return null;

  const normalizedImages = Array.isArray(raw.images)
    ? raw.images
      .map((item) => (typeof item === "string" ? { src: item } : item))
      .filter((item) => item?.src && typeof item.src === "string")
    : [];

  let primaryImage = "";

  if (typeof raw.image === "string" && raw.image.trim()) {
    primaryImage = raw.image.trim();
  } else if (raw.image?.src && typeof raw.image.src === "string") {
    primaryImage = raw.image.src.trim();
  } else if (normalizedImages.length > 0) {
    primaryImage = normalizedImages[0].src;
  }

  return {
    ...raw,
    product_type: raw.product_type || raw.productType || "-",
    image: primaryImage,
    images: normalizedImages,
    tags: Array.isArray(raw.tags)
      ? raw.tags.join(", ")
      : typeof raw.tags === "string" ? raw.tags : "",
    variants: Array.isArray(raw.variants) ? raw.variants : [],
  };
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [apiProduct, setApiProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mainImageFailed, setMainImageFailed] = useState(false);
  const [imageSrc, setImageSrc] = useState("");   // ← New state for image with fallback

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch product
  useEffect(() => {
    let isMounted = true;

    const fetchProductById = async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(`shopify/getProductById/${id}`);
        const productData = data?.data || data;

        if (isMounted && productData) {
          const normalized = normalizeProduct(productData);
          setApiProduct(normalized);
        }
      } catch (error) {
        const errMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch product details";
        toast.error(errMsg);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (id) fetchProductById();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const product = apiProduct; // No double normalization

  const variants = product?.variants || [];
  const tags = product?.tags
    ? product.tags.split(",").map((item) => item.trim()).filter(Boolean)
    : [];

  const firstVariant = variants[0] || {};
  const totalStock = variants.reduce(
    (sum, variant) => sum + Number(variant?.inventory_quantity || 0),
    0
  );

  // 1. FIRST define gallery
  const gallery = useMemo(() => {
    const imgs = product?.images || [];

    return imgs
      .map((img) => img?.src)
      .filter(Boolean)
      .filter((src, index, arr) => arr.indexOf(src) === index);
  }, [product?.images]);

  const openPreview = (src) => {
    const index = gallery.indexOf(src);
    setCurrentIndex(index >= 0 ? index : 0);
    setPreviewImage(src);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewImage("");
  };

  const showNext = useCallback(() => {
    if (!gallery.length) return;

    const nextIndex = (currentIndex + 1) % gallery.length;
    setCurrentIndex(nextIndex);
    setPreviewImage(gallery[nextIndex]);
  }, [currentIndex, gallery]);

  const showPrev = useCallback(() => {
    if (!gallery.length) return;

    const prevIndex = (currentIndex - 1 + gallery.length) % gallery.length;
    setCurrentIndex(prevIndex);
    setPreviewImage(gallery[prevIndex]);
  }, [currentIndex, gallery]);

  // Reset image when product changes
  useEffect(() => {
    if (product?.image) {
      setImageSrc(product.image);
      setMainImageFailed(false);
    }
  }, [product?.image]);

  useEffect(() => {
    if (!previewOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closePreview();
      } else if (e.key === "ArrowRight") {
        showNext();
      } else if (e.key === "ArrowLeft") {
        showPrev();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewOpen, showNext, showPrev]);

  const discountPercent = getDiscountPercent(firstVariant?.price, firstVariant?.compare_at_price);
  const status = (product?.status || "").toString().toLowerCase();
  const statusLabel = product?.status || "-";

  // Image fallback handler
  const handleImageError = () => {
    console.log("❌ Image failed to load:", imageSrc);

    const currentIndex = gallery.indexOf(imageSrc);
    if (currentIndex >= 0 && currentIndex < gallery.length - 1) {
      console.log("Trying next image...");
      setImageSrc(gallery[currentIndex + 1]);
      setMainImageFailed(false);
    } else {
      setMainImageFailed(true);
    }
  };

  if (loading && !product) {
    return (
      <div className="content-wrapper pd-top">
        <div className="page-header mt-5 d-flex justify-content-between align-items-center">
          <h3 className="page-title">Product Details</h3>
          <button className="btn btn-primary" onClick={() => navigate("/products")}>
            Back to Products
          </button>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="mb-0 text-muted">Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="content-wrapper pd-top">
        <div className="page-header mt-5 d-flex justify-content-between align-items-center">
          <h3 className="page-title">Product Details</h3>
          <button className="btn btn-primary" onClick={() => navigate("/products")}>
            Back to Products
          </button>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="mb-0 text-muted">Product not found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper pd-top product-detail-page">
      <div className="pd-topbar-modern" style={{ marginTop: "40px", marginBottom: "15px" }}>
        <div className="pd-topbar-left">
          <div className="pd-meta-line">

            {product?.vendor && (
              <div className="pd-meta-item vendor">
                <span className="pd-meta-label">Vendor</span>
                <span className="pd-meta-value">{product.vendor}</span>
              </div>
            )}

            {product?.product_type && (
              <div className="pd-meta-item type">
                <span className="pd-meta-label">Product Type</span>
                <span className="pd-meta-value">{product.product_type}</span>
              </div>
            )}

          </div>
        </div>

        <div className="pd-topbar-right">
          <button
            className="pd-back-btn"
            onClick={() => navigate("/products")}
          >
            ← Back
          </button>
        </div>
      </div>

      <div className="pd-card pd-hero mb-4">
        <div className="card-body">
          <div className="pd-hero-grid">
            {/* ==================== MEDIA SECTION ==================== */}
            <div className="pd-media">
              <div className="pd-media-main">
                {imageSrc && !mainImageFailed ? (
                  <img
                    src={imageSrc}
                    alt={product?.title || "Product"}
                    loading="lazy"
                    onError={handleImageError}
                    onClick={() => openPreview(imageSrc)}
                    style={{ cursor: "pointer" }}
                  />
                ) : (
                  <div
                    className="border rounded p-5 text-center text-muted d-flex flex-column align-items-center justify-content-center"
                    style={{ minHeight: "420px", background: "#f8f9fa" }}
                  >
                    <p className="mb-2 fw-semibold text-danger">Missing Image?</p>
                    <small className="text-muted text-center">
                      Could not load image
                      <br />
                      <code
                        style={{
                          fontSize: "0.75rem",
                          wordBreak: "break-all",
                          display: "block",
                          marginTop: "8px",
                        }}
                      >
                        {imageSrc || "No URL available"}
                      </code>
                    </small>
                  </div>
                )}
              </div>

            </div>

            {/* Meta Information */}
            <div className="pd-meta">

              {/* Title */}
              <h4 className="pd-title-main">
                {product?.title || "-"}
              </h4>

              {/* Badges */}
              <div className="pd-meta-badges">
                <span className={`pd-badge ${status === "active" ? "success" : "warning"}`}>
                  {statusLabel.toUpperCase()}
                </span>

                <span className="pd-badge neutral">
                  {totalStock} in stock
                </span>
              </div>

              {/* Slug */}
              {/* {product?.handle && (
                <div className="pd-slug-box">
                  <span>Slug</span>
                  <p title={product.handle}>{product.handle}</p>
                </div>
              )} */}

              {/* Price */}
              <div className="pd-price-box">
                <div className="pd-price-main">
                  {toCurrency(firstVariant?.price)}
                </div>

                {firstVariant?.compare_at_price && (
                  <div className="pd-price-old">
                    {toCurrency(firstVariant.compare_at_price)}
                  </div>
                )}

                {discountPercent && (
                  <span className="pd-discount-pill">
                    Save {discountPercent}%
                  </span>
                )}
              </div>

              {/* Info Grid */}
              <div className="pd-info-cards">

                <div className="pd-info-card">
                  <span>SKU</span>
                  <p>{firstVariant?.sku || "-"}</p>
                </div>

                <div className="pd-info-card">
                  <span>Product ID</span>
                  <p>{product?.id || "-"}</p>
                </div>

                <div className="pd-info-card">
                  <span>Created</span>
                  <p>{formatDate(product?.createdAt)}</p>
                </div>

                <div className="pd-info-card">
                  <span>Updated</span>
                  <p>{formatDate(product?.updatedAt)}</p>
                </div>

                <div className="pd-info-card full">
                  <span>Primary Variant</span>
                  <p>{firstVariant?.title || "-"}</p>
                </div>

              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Rest of your sections remain the same */}
      <div className="pd-card mb-4">
        <div className="card-body">
          <h5 className="pd-section-title">Description</h5>
          <div className="pd-muted" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: product?.body_html || "<p>-</p>" }} />
        </div>
      </div>

      <div className="pd-card mb-4">
        <div className="card-body">
          <h5 className="pd-section-title">Tags</h5>
          {tags.length > 0 ? (
            <div className="pd-tags">
              {tags.map((tag) => (
                <span key={tag} className="pd-tag">
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <p className="pd-muted mb-0">No tags available</p>
          )}
        </div>
      </div>

      <div className="pd-card mb-4">
        <div className="card-body">
          <h5 className="pd-section-title">Gallery</h5>
          {gallery.length > 0 ? (
            <div className="row">
              {gallery.slice(0, 8).map((src, index) => (
                <div className="col-md-3 col-sm-4 col-6 mb-3" key={src}>
                  <div
                    className="w-100 d-block"
                    style={{ padding: 0, cursor: "pointer" }}
                    onClick={() => openPreview(src)}
                    title="Preview image"
                  >
                    <div className="pd-gallery-tile">
                      <img
                        src={src}
                        alt={`Product ${index + 1}`}
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="pd-muted mb-0">No images available</p>
          )}
        </div>
      </div>

      <div className="pd-card mb-4 pd-variants-card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="pd-section-title mb-0">Variants</h5>
            <span className="pd-count-badge">{variants.length} items</span>
          </div>

          <div className="pd-table-wrapper">
            <table className="pd-premium-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Variant</th>
                  <th>SKU</th>
                  <th>Price</th>
                  <th>Compare</th>
                  <th>Stock</th>
                  <th>Weight</th>
                </tr>
              </thead>

              <tbody>
                {variants.length > 0 ? (
                  variants.map((variant, index) => {
                    const stock = variant?.inventory_quantity ?? 0;

                    return (
                      <tr key={variant?.id || index}>
                        <td className="pd-index">{index + 1}</td>

                        <td className="pd-variant-name">
                          {variant?.title || "-"}
                        </td>

                        <td className="pd-sku">
                          {variant?.sku || "-"}
                        </td>

                        <td className="pd-price">
                          {toCurrency(variant?.price)}
                        </td>

                        <td className="pd-compare">
                          {variant?.compare_at_price
                            ? toCurrency(variant?.compare_at_price)
                            : "-"}
                        </td>

                        <td>
                          <span
                            className={`pd-stock-badge ${stock > 0 ? "in" : "out"
                              }`}
                          >
                            {stock > 0 ? `${stock} In Stock` : "Out of Stock"}
                          </span>
                        </td>

                        <td className="pd-weight">
                          {variant?.weight ?? "-"}{" "}
                          <span>{variant?.weight_unit || ""}</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No variants available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="pd-card pd-system-card">
        <div className="card-body">
          <h5 className="pd-section-title mb-3">System Info</h5>

          <div className="pd-info-grid">

            <div className="pd-info-item">
              <span className="pd-info-label">Primary Variant ID</span>
              <span className="pd-info-value">
                {firstVariant?.id || "-"}
              </span>
            </div>

            <div className="pd-info-item">
              <span className="pd-info-label">Inventory Item ID</span>
              <span className="pd-info-value">
                {firstVariant?.inventory_item_id || "-"}
              </span>
            </div>

            <div className="pd-info-item">
              <span className="pd-info-label">Weight Unit</span>
              <span className="pd-info-value">
                {firstVariant?.weight_unit || "-"}
              </span>
            </div>

            <div className="pd-info-item">
              <span className="pd-info-label">Product Type</span>
              <span className="pd-info-value highlight">
                {product?.product_type || "-"}
              </span>
            </div>

          </div>
        </div>
      </div>

      {previewOpen && (
        <div className="pd-image-preview-overlay" onClick={closePreview}>
          <div className="pd-image-preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="pd-image-close" onClick={closePreview}>
              ✕
            </button>

            <img src={previewImage} alt="Preview" />

            {/* Optional Thumbnails */}
            {gallery.length > 1 && (
              <div className="pd-preview-thumbs">
                {gallery.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`Preview ${i + 1}`}
                    className={img === previewImage ? "active" : ""}
                    onClick={() => setPreviewImage(img)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;